// WebSocket message types for Casino API
// These types correspond to the message structures defined in the backend WebSocket handlers

// --- Unified Input Events (buttons + touch) ---

export type InputEventType = 'button' | 'touch';

export enum ButtonAction {
  RELEASED = 0,
  PRESSED = 1
}

export type TouchAction = 'press' | 'release' | 'move';

// Outgoing input event (Client → Server)
export interface InputEventOutgoing {
  type: InputEventType;
  machine_id: string;
  session_id: string;
  timestamp: string;
  // Button-specific
  button_id?: number;
  button_action?: ButtonAction;
  // Touch-specific
  x?: number;
  y?: number;
  touch_action?: TouchAction;
}

// Incoming input event acknowledgment (Server → Client)
export interface InputEventIncoming {
  type: InputEventType;
  machine_id: string;
  session_id: string;
  timestamp: string;
  success: boolean;
  error_message?: string;
  // Button-specific
  button_id?: number;
  button_action?: ButtonAction;
  // Touch-specific
  x?: number;
  y?: number;
  touch_action?: TouchAction;
}

// Legacy types (kept for backward compatibility)
export interface ButtonEventOutgoing {
  machine_id: string;
  session_id: string;
  button_id: number;
  action: ButtonAction;
  timestamp: string;
}

export interface ButtonEventIncoming {
  machine_id: string;
  session_id: string;
  button_id: number;
  action: ButtonAction;
  success: boolean;
  error_message?: string;
  timestamp: string;
}

// Machine Status Stream Messages
export interface MachineStatusUpdate {
  type: 'status_update';
  payload: {
    machine_id: string;
    old_status: string;
    new_status: string;
    timestamp: string;
    platform_id: string;
    category: string;
    name: string;
  };
}

export interface MachineListInitialData {
  pagination: {
    page_info: {
      limit: number;
      offset: number;
    };
    total_count: number;
  };
  machines: {
    machines: Array<{
      id: string;
      name: string;
      status: string;
      category: string;
      last_seen: string;
      stream_url?: string;
      stream_auth_token?: string;
    }>;
  };
}

// Union type for all possible WebSocket messages
export type WebSocketMessage =
  | InputEventIncoming
  | ButtonEventIncoming
  | MachineStatusUpdate
  | MachineListInitialData;

// WebSocket connection states
export interface WebSocketStatus {
  connected: boolean;
  error: string | null;
  reconnectAttempts: number;
}

// Configuration for WebSocket connections
export interface WebSocketConfig {
  autoReconnect?: boolean;
  maxReconnectAttempts?: number;
  reconnectDelay?: number;
}
